/**
 * Shared source syntax for configurable event fields.
 *
 * Classifies an authored value as a data path, a literal, empty, or
 * invalid. Event-family rules (Product wire keys, Element allowlists)
 * live in the callers, not here.
 */

/**
 * Path mapping against a resolve root.
 */
export type FieldSourceDataPath = {
  readonly dataPath: string;
  readonly literal?: never;
};

/**
 * Literal mapping. Callers decide which keys may use this form.
 */
export type FieldSourceLiteral = {
  readonly literal: string;
  readonly dataPath?: never;
};

/**
 * Shared source syntax for configurable event fields.
 */
export type FieldSource = string | FieldSourceDataPath | FieldSourceLiteral;

export type FieldSourceParseResult =
  | { readonly kind: 'dataPath'; readonly dataPath: string }
  | { readonly kind: 'literal'; readonly literal: string }
  | { readonly kind: 'empty' }
  | {
      readonly kind: 'invalid';
      readonly reason: 'both-dataPath-and-literal' | 'malformed';
    };

/**
 * One `tracking.customDimensions` entry. Sibling of field maps,
 * never nested inside a field value. Literals are allowed.
 */
export type CustomDimensionMapping =
  | {
      readonly key: string;
      readonly dataPath: string;
      readonly literal?: never;
    }
  | {
      readonly key: string;
      readonly literal: string;
      readonly dataPath?: never;
    };

/**
 * Classification of one `customDimensions` array entry.
 */
export type CustomDimensionParseResult =
  | {
      readonly kind: 'dataPath';
      readonly key: string;
      readonly dataPath: string;
    }
  | { readonly kind: 'literal'; readonly key: string; readonly literal: string }
  | { readonly kind: 'empty' }
  | {
      readonly kind: 'invalid';
      readonly reason:
        | 'both-dataPath-and-literal'
        | 'malformed'
        | 'missing-key';
    };

/**
 * Classify a shared event field source without applying event-family rules.
 *
 * @param {unknown} value - Raw field mapping
 * @returns {FieldSourceParseResult} Parse classification
 */
export function parseFieldSource(value: unknown): FieldSourceParseResult {
  if (value === undefined || value === null) {
    return { kind: 'empty' };
  }
  if (typeof value === 'string') {
    return value.length === 0
      ? { kind: 'empty' }
      : { kind: 'dataPath', dataPath: value };
  }
  if (!isPlainObject(value)) {
    return { kind: 'invalid', reason: 'malformed' };
  }

  const hasDataPath = Object.prototype.hasOwnProperty.call(value, 'dataPath');
  const hasLiteral = Object.prototype.hasOwnProperty.call(value, 'literal');
  if (hasDataPath && hasLiteral) {
    return { kind: 'invalid', reason: 'both-dataPath-and-literal' };
  }
  if (hasDataPath) {
    if (typeof value.dataPath !== 'string') {
      return { kind: 'invalid', reason: 'malformed' };
    }
    return value.dataPath.length === 0
      ? { kind: 'empty' }
      : { kind: 'dataPath', dataPath: value.dataPath };
  }
  if (hasLiteral) {
    const literal = value.literal;
    if (typeof literal === 'number' && Number.isFinite(literal)) {
      return { kind: 'literal', literal: String(literal) };
    }
    if (typeof literal === 'boolean') {
      return { kind: 'literal', literal: String(literal) };
    }
    if (typeof literal !== 'string') {
      return { kind: 'invalid', reason: 'malformed' };
    }
    return literal.length === 0
      ? { kind: 'empty' }
      : { kind: 'literal', literal };
  }
  return { kind: 'invalid', reason: 'malformed' };
}

/**
 * Classify one sibling `tracking.customDimensions` entry.
 *
 * `null` / `undefined` are unset. Other non-objects are malformed
 * (including empty strings), matching Product extract/apply.
 *
 * @param {unknown} value - Raw array element
 * @returns {CustomDimensionParseResult} Parse classification
 */
export function parseCustomDimensionMapping(
  value: unknown,
): CustomDimensionParseResult {
  if (value === undefined || value === null) {
    return { kind: 'empty' };
  }
  if (!isPlainObject(value)) {
    return { kind: 'invalid', reason: 'malformed' };
  }

  const key = asNonEmptyString(value.key);
  if (key === undefined) {
    return { kind: 'invalid', reason: 'missing-key' };
  }

  const parsed = parseFieldSource(value);
  if (parsed.kind === 'dataPath') {
    return { ...parsed, key };
  }
  if (parsed.kind === 'literal') {
    return { ...parsed, key };
  }
  return parsed;
}

/**
 * Narrow unknown to a non-array object.
 *
 * @param {unknown} value - Candidate object
 * @returns {boolean} True when `value` is a plain object
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Return a non-empty string, otherwise undefined.
 *
 * @param {unknown} value - Candidate string
 * @returns {string | undefined} The string when non-empty
 */
function asNonEmptyString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}
